/**
 * 첨부파일 패널 - PDF 뷰어 표시 영역
 * 플로팅 폴더는 BookViewer에서 직접 렌더링
 */

import React, { useCallback } from 'react';
import styled from 'styled-components';
import PdfViewer from './PdfViewer';
import { Attachment } from '../../services/attachmentService';

// ============================================================================
// Styled Components
// ============================================================================

const PanelContainer = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary, #f5f5f5);
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const PdfViewerWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TitleBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  background: ${props => props.theme.colors.white};
  border-bottom: 1px solid ${props => props.theme.colors.border.ce};
`;

const TitleText = styled.span`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 500;
  color: ${props => props.theme.colors.book.color4b};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const TitleBarButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
  height: 100%;
`;

const IconButton = styled.button`
  height: 100%;
  width: 48px;
  padding:0 12px;
  border-left: 1px solid ${props => props.theme.colors.border.ce};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
  outline: none;

  svg {
    width: 14px;
    height: 14px;
  }

  html.dark-mode & svg {
    filter: invert(1);
  }
`;

const PositionToggleButton = styled.button<{ $panelOnLeft?: boolean }>`
  height: 100%;
  width: 48px;
  padding:0 12px;
  border-left: 1px solid ${props => props.theme.colors.border.ce};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  
  svg {
    width: 16px;
    height: 16px;
  }
  html.dark-mode & svg {
    filter: invert(1);
  }
`;

// ============================================================================
// Props
// ============================================================================

export interface AttachmentPanelProps {
  bookUid: string;
  openPdf: Attachment;
  onMinimize: () => void;
  onClose: (attachment: Attachment) => void;
  panelOnLeft?: boolean;
  onPositionToggle?: () => void;
}

// ============================================================================
// Component
// ============================================================================

const AttachmentPanel: React.FC<AttachmentPanelProps> = ({
  bookUid,
  openPdf,
  onMinimize,
  onClose,
  panelOnLeft = true,
  onPositionToggle,
}) => {
  const handleMinimize = useCallback(() => {
    onMinimize();
  }, [onMinimize]);

  const handleClose = useCallback(() => {
    onClose(openPdf);
  }, [onClose, openPdf]);

  return (
    <PanelContainer>
      <PdfViewerWrapper>
        <TitleBar>
          <TitleText title={openPdf.file_name}>{openPdf.file_name}</TitleText>
          <TitleBarButtons>
            {onPositionToggle && (
              <PositionToggleButton
                $panelOnLeft={panelOnLeft}
                onClick={onPositionToggle}
                title={panelOnLeft ? '오른쪽으로 이동' : '왼쪽으로 이동'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {panelOnLeft ? (
                    <path d="M13 17l5-5-5-5M6 17l5-5-5-5" />
                  ) : (
                    <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
                  )}
                </svg>
              </PositionToggleButton>
            )}
            <IconButton type="button" onClick={handleMinimize} title="최소화 (폴더에 유지)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </IconButton>
            <IconButton type="button" onClick={handleClose} title="닫기 (폴더에서 제거)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </IconButton>
          </TitleBarButtons>
        </TitleBar>
        <PdfViewer
          bookUid={bookUid}
          fileUuid={openPdf.file_uuid}
          fileName={openPdf.file_name}
        />
      </PdfViewerWrapper>
    </PanelContainer>
  );
};

export default AttachmentPanel;
